const CURRENCY_GLYPH = {
  USD: "$",
  EUR: "€",
  UAH: "₴",
};

const PROPERTY_TYPE_LABELS = {
  apartment: "Квартира",
  house: "Будинок",
  land: "Земельна ділянка",
  commercial: "Комерційна",
  industrial: "Промислова",
};

const DEAL_TYPE_LABELS = {
  sale: "Продаж",
  rent: "Оренда",
};

export const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const numeric = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(numeric)) return "—";
  const formatted = numeric
    .toLocaleString("uk-UA", { maximumFractionDigits: 2 })
    .replace(/\u00a0/g, " ");
  const glyph = CURRENCY_GLYPH[currency] || "";
  return `${glyph}${formatted}`.trim();
};

export const formatCommissionPercent = (percent) => {
  if (percent === null || percent === undefined || percent === "") return "—";
  const numeric = typeof percent === "number" ? percent : Number(percent);
  if (!Number.isFinite(numeric)) return "—";
  return `${numeric.toLocaleString("uk-UA", { maximumFractionDigits: 2 })}%`;
};

export const formatCreatedDateUk = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("uk-UA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export const formatDateTimeUk = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("uk-UA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const propertyTypeLabel = (propertyType) =>
  PROPERTY_TYPE_LABELS[propertyType] || propertyType || "—";

export const dealTypeLabel = (dealType) =>
  DEAL_TYPE_LABELS[dealType] || dealType || "—";

export const partyDisplayName = (user) => {
  if (!user) return "—";
  return user.fullname || user.handle || user.email || "—";
};

export const propertyTitleOrFallback = (property, propertyId) => {
  if (property && property.title) return property.title;
  if (propertyId != null) return `Об'єкт #${propertyId}`;
  return "—";
};

/**
 * Prefer live property.title, then Deal property_title_snapshot, then id.
 */
export const propertyTitleFromDeal = (deal, property) => {
  if (property && property.title) return property.title;
  if (deal && deal.property_title_snapshot) {
    return deal.property_title_snapshot;
  }
  const propertyId =
    (deal && deal.property) ||
    (property && property.id) ||
    null;
  if (propertyId != null) return `Об'єкт #${propertyId}`;
  return "—";
};

/**
 * Prefer frozen compensation_* fields; fall back to legacy realtor_commission_*.
 * Fixed type must not show a spurious 0% line.
 */
export const formatDealCompensation = (deal) => {
  if (!deal) return "—";

  const type = deal.compensation_type;
  if (type === "fixed") {
    const amount =
      deal.compensation_amount != null
        ? deal.compensation_amount
        : deal.compensation_value;
    const currency =
      deal.compensation_currency || deal.currency || "";
    return formatMoney(amount, currency);
  }

  if (type === "percent") {
    const percent =
      deal.compensation_value != null
        ? deal.compensation_value
        : deal.realtor_commission_percent;
    const amount =
      deal.compensation_amount != null
        ? deal.compensation_amount
        : deal.realtor_commission_amount;
    const currency =
      deal.compensation_currency || deal.currency || "";
    const pct = formatCommissionPercent(percent);
    const money = formatMoney(amount, currency);
    if (pct === "—" && money === "—") return "—";
    if (pct === "—") return money;
    if (money === "—") return pct;
    return `${pct} · ${money}`;
  }

  // Legacy / missing compensation_type — realtor_commission_* aliases.
  const pct = formatCommissionPercent(deal.realtor_commission_percent);
  const money = formatMoney(
    deal.realtor_commission_amount,
    deal.currency,
  );
  if (pct === "—" && money === "—") return "—";
  if (pct === "—") return money;
  if (money === "—") return pct;
  return `${pct} · ${money}`;
};

export const formatLocationSummary = (location) => {
  if (!location) return "—";
  if (location.formatted_address) return location.formatted_address;
  const settlementName = location.settlement_name
    || (location.settlement_unit && location.settlement_unit.name)
    || "";
  const parts = [
    settlementName,
    location.local_area,
    location.region_name,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "—";
};

export const getPropertyMainImageUrl = (property) => {
  if (!property) return null;
  if (property.main_image) return property.main_image;
  const images = Array.isArray(property.images) ? property.images : [];
  const main = images.find((img) => img.is_main && img.kind === "image") || images[0];
  if (!main) return null;
  return main.image || main.url || null;
};
